import { DownloadOutlined, FileExcelOutlined } from '@ant-design/icons'
import { Modal, Upload, message } from 'antd'
import { useEffect, useState } from 'react'
import {
    downloadTestCaseTemplate,
    importTestCasesExcel,
} from '../../../redux/testRun/testRun.service'
import styles from './ImportTestCasesModal.module.css'

const ImportTestCasesModal = ({
    isOpen = false,
    setOpen = () => {},
    projectId,
    onSuccess = () => {},
}) => {
    const [fileList, setFileList] = useState([])
    const [loading, setLoading] = useState(false)
    const [downloading, setDownloading] = useState(false)

    useEffect(() => {
        if (!isOpen) {
            setFileList([])
        }
    }, [isOpen])

    const handleClose = () => {
        if (loading) return
        setFileList([])
        setOpen(false)
    }

    const handleDownloadTemplate = async () => {
        setDownloading(true)
        try {
            const result = await downloadTestCaseTemplate()
            if (!result?.success) {
                message.error(result?.message || 'Failed to download template')
            }
        } finally {
            setDownloading(false)
        }
    }

    const handleImport = async () => {
        if (!fileList.length) {
            message.warning('Please select a file to import')
            return
        }

        if (!projectId) {
            message.error('Project is required to import test cases')
            return
        }

        setLoading(true)
        try {
            const file = fileList[0].originFileObj || fileList[0]
            const res = await importTestCasesExcel({ file, projectId })
            const data = res?.data ?? {}

            if (data?.success) {
                message.success(data?.message || 'Test cases imported successfully')
                setFileList([])
                handleClose()
                onSuccess()
                return
            }

            const errorLines = Array.isArray(data?.errors) ? data.errors : []
            if (errorLines.length) {
                message.error({
                    content: (
                        <div>
                            <div>{data?.message || 'Import failed'}</div>
                            <ul className={styles.errorList}>
                                {errorLines.map((err) => (
                                    <li key={err}>{err}</li>
                                ))}
                            </ul>
                        </div>
                    ),
                    duration: 8,
                })
            } else {
                message.error(data?.message || 'Failed to import test cases')
            }
        } catch {
            message.error('Failed to import test cases')
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal
            title={null}
            open={isOpen}
            onCancel={handleClose}
            footer={null}
            width={560}
            centered
            destroyOnClose
            className={styles.modal}
        >
            <div className={styles.shell}>
                <div className={styles.header}>
                    <div className={styles.headerIcon}>
                        <FileExcelOutlined />
                    </div>
                    <div>
                        <h3 className={styles.title}>Import test cases</h3>
                        <p className={styles.subtitle}>
                            Upload an Excel file (.xlsx, .xls) to add multiple test
                            cases to this project at once.
                        </p>
                    </div>
                </div>

                <button
                    type="button"
                    className={styles.templateButton}
                    onClick={handleDownloadTemplate}
                    disabled={downloading}
                >
                    <DownloadOutlined />
                    <span>
                        {downloading ? 'Downloading...' : 'Download template'}
                    </span>
                </button>

                <Upload.Dragger
                    className={styles.uploader}
                    fileList={fileList}
                    onChange={({ fileList: nextList }) => setFileList(nextList)}
                    beforeUpload={() => false}
                    accept=".xlsx,.xls"
                    maxCount={1}
                    onRemove={() => setFileList([])}
                >
                    <div className={styles.uploaderInner}>
                        <FileExcelOutlined className={styles.uploaderIcon} />
                        <p className={styles.uploaderTitle}>
                            Select or drop an Excel file
                        </p>
                        <p className={styles.uploaderHint}>
                            Columns: Title, Preconditions, Testdata, Steps,
                            Description, Expected Result, Sprint, Tags
                        </p>
                    </div>
                </Upload.Dragger>

                <div className={styles.actions}>
                    <button
                        type="button"
                        className={styles.cancelButton}
                        onClick={handleClose}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={styles.importButton}
                        onClick={handleImport}
                        disabled={!fileList.length || loading}
                    >
                        {loading ? 'Importing...' : 'Import'}
                    </button>
                </div>
            </div>
        </Modal>
    )
}

export default ImportTestCasesModal
